import { WS_METHODS } from "@t3tools/contracts";
import { Atom } from "effect/unstable/reactivity";

import type { EnvironmentRegistry } from "../connection/registry.ts";
import { createAtomCommandScheduler, createEnvironmentRpcCommand } from "./runtime.ts";

export function createVmEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const lifecycleScheduler = createAtomCommandScheduler();
  const controlScheduler = createAtomCommandScheduler();
  // Reads and writes for one resource share a serial lane. Separate schedulers
  // keep profile, secret, and build responses independent.
  const profileScheduler = createAtomCommandScheduler();
  const secretsScheduler = createAtomCommandScheduler();
  const buildsScheduler = createAtomCommandScheduler();
  const byThread = ({
    environmentId,
    input,
  }: {
    environmentId: string;
    input: { threadId: string };
  }) => JSON.stringify([environmentId, input.threadId]);
  const byProject = ({
    environmentId,
    input,
  }: {
    environmentId: string;
    input: { projectId: string };
  }) => JSON.stringify([environmentId, input.projectId]);

  return {
    status: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:status",
      tag: WS_METHODS.vmStatus,
    }),
    ensure: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:ensure",
      tag: WS_METHODS.vmEnsure,
      scheduler: lifecycleScheduler,
      concurrency: { mode: "serial", key: byThread },
    }),
    resume: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:resume",
      tag: WS_METHODS.vmResume,
      scheduler: lifecycleScheduler,
      concurrency: { mode: "serial", key: byThread },
    }),
    stop: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:stop",
      tag: WS_METHODS.vmStop,
      scheduler: lifecycleScheduler,
      concurrency: { mode: "serial", key: byThread },
    }),
    frame: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:frame",
      tag: WS_METHODS.vmFrame,
    }),
    controlStatus: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:control-status",
      tag: WS_METHODS.vmControlStatus,
    }),
    takeControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:control-take",
      tag: WS_METHODS.vmControlTake,
      scheduler: controlScheduler,
      concurrency: { mode: "serial", key: byThread },
    }),
    heartbeatControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:control-heartbeat",
      tag: WS_METHODS.vmControlHeartbeat,
      scheduler: controlScheduler,
      concurrency: { mode: "latest", key: byThread },
    }),
    returnControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:control-return",
      tag: WS_METHODS.vmControlReturn,
      scheduler: controlScheduler,
      concurrency: { mode: "serial", key: byThread },
    }),
    input: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:input",
      tag: WS_METHODS.vmInput,
    }),
    profileGet: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:profile-get",
      tag: WS_METHODS.vmProfileGet,
      scheduler: profileScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    profileProposalGet: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:profile-proposal-get",
      tag: WS_METHODS.vmProfileProposalGet,
      scheduler: profileScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    profileConfigure: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:profile-configure",
      tag: WS_METHODS.vmProfileConfigure,
      scheduler: profileScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    secretsList: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:secrets-list",
      tag: WS_METHODS.vmSecretsList,
      scheduler: secretsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    secretsPut: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:secrets-put",
      tag: WS_METHODS.vmSecretsPut,
      scheduler: secretsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    secretDelete: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:secret-delete",
      tag: WS_METHODS.vmSecretDelete,
      scheduler: secretsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    buildsList: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:builds-list",
      tag: WS_METHODS.vmBuildsList,
      scheduler: buildsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    buildSnapshot: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:build-snapshot",
      tag: WS_METHODS.vmBuildSnapshot,
      scheduler: buildsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    buildActivate: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:build-activate",
      tag: WS_METHODS.vmBuildActivate,
      scheduler: buildsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
    buildDelete: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:vm:build-delete",
      tag: WS_METHODS.vmBuildDelete,
      scheduler: buildsScheduler,
      concurrency: { mode: "serial", key: byProject },
    }),
  };
}
